/**
 * ABN and ACN checksum validation.
 *
 * Catches typos, not fabrication — a checksum-valid ABN may still not be
 * registered. Verifying against the ABR is a separate, later thing
 * (docs/roadmap.md).
 */

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19] as const;
const ACN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 1] as const;

export function normaliseAbn(input: string): string {
  return input.replace(/[\s-]/g, '');
}

/** 11 digits. Subtract 1 from the first, weight, sum, must divide by 89. */
export function isValidAbn(input: string): boolean {
  const digits = normaliseAbn(input);
  if (!/^\d{11}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const weight = ABN_WEIGHTS[i]!;
    let digit = Number(digits[i]);
    if (i === 0) digit -= 1;
    sum += digit * weight;
  }

  return sum % 89 === 0;
}

/** 9 digits. Weighted sum of the first 8; last digit is the check digit. */
export function isValidAcn(input: string): boolean {
  const digits = normaliseAbn(input);
  if (!/^\d{9}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += Number(digits[i]) * ACN_WEIGHTS[i]!;
  }

  const remainder = sum % 10;
  const check = remainder === 0 ? 0 : 10 - remainder;

  return check === Number(digits[8]);
}

/** Accepts either, since the form asks for "ABN / ACN". */
export function isValidAbnOrAcn(input: string): boolean {
  const digits = normaliseAbn(input);
  if (digits.length === 11) return isValidAbn(digits);
  if (digits.length === 9) return isValidAcn(digits);
  return false;
}

/** Display formatting: 12 345 678 901 / 123 456 789. */
export function formatAbn(input: string): string {
  const d = normaliseAbn(input);
  if (d.length === 11) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
  if (d.length === 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  return input;
}
